import React, { useState } from 'react';
import { Input, InputGroup, Message, Tag } from 'rsuite';
import { Button, ConfirmModal, Modal } from '@nsi-advisory-services/components';
import { showToast } from '../../utils/toast';
import { useAddInvite, useInvites, useRemoveInvite } from '../../hooks/useQueries';

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US') : '';
};

// Who may sign in. Inviting an address lets that person sign in once with it;
// their employee record is created the first time they do.
export default function InvitesModal({ open, onClose }) {
  const invitesQuery = useInvites({ enabled: open });
  const addInvite = useAddInvite();
  const removeInvite = useRemoveInvite();

  const [email, setEmail] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const invites = invitesQuery.data || [];

  const submitInvite = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    try {
      await addInvite.mutateAsync(address);
      showToast('success', `Invited ${address}.`);
      setEmail('');
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to add the invite'));
    }
  };

  const doRemove = async () => {
    if (!confirmRemove) return;
    try {
      await removeInvite.mutateAsync(confirmRemove.id);
      showToast('success', `Removed the invite for ${confirmRemove.email}.`);
      setConfirmRemove(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to remove the invite'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>Invites</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={submitInvite} className="mb-3" style={{ maxWidth: 480 }}>
          <InputGroup>
            <Input
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="name@company.com"
              aria-label="Email to invite"
            />
            <InputGroup.Button type="submit" appearance="primary" loading={addInvite.isPending} disabled={!email.trim()}>
              Invite
            </InputGroup.Button>
          </InputGroup>
        </form>

        <div className="nsi-table-wrap">
          <div className="bdc-table-head">
            <div className="bdc-table-head__heading">
              <h3 className="bdc-table-head__title">Invites</h3>
              <span className="bdc-count">{invites.length}</span>
            </div>
          </div>

          {invitesQuery.error ? (
            <Message type="error" showIcon>
              {String(invitesQuery.error?.message || invitesQuery.error)}
            </Message>
          ) : null}

          <table className="nsi-table bdc-table--narrow">
            <thead>
              <tr>
                <th>Email</th>
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 220 }}>Invited by</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {invitesQuery.isLoading ? (
                <tr>
                  <td colSpan={4} className="nsi-table__empty">Loading…</td>
                </tr>
              ) : invites.length === 0 ? (
                <tr>
                  <td colSpan={4} className="nsi-table__empty">No invites yet.</td>
                </tr>
              ) : invites.map((invite) => (
                <tr key={invite.id}>
                  <td className="bdc-table-wrap">{invite.email}</td>
                  <td>
                    {invite.accepted_at
                      ? <Tag color="green" size="sm">Signed in</Tag>
                      : <Tag color="orange" size="sm">Pending</Tag>}
                  </td>
                  <td className="bdc-table-wrap">
                    {invite.invited_by || <span className="nsi-table__muted">—</span>}
                    <span className="nsi-table__muted"> · {formatDate(invite.created_at)}</span>
                  </td>
                  <td className="nsi-table__right">
                    {invite.accepted_at ? null : (
                      <Button size="sm" variant="danger" onClick={() => setConfirmRemove(invite)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <ConfirmModal
        open={!!confirmRemove}
        title="Remove Invite"
        body={`Remove the invite for ${confirmRemove?.email}? They won't be able to sign in unless invited again.`}
        confirmText="Remove"
        danger
        loading={removeInvite.isPending}
        onConfirm={doRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </Modal>
  );
}
